import React, { useState, useEffect } from "react";
import { fetchPropertyAnalysis } from "../../services/api";

const PropertySummaryModal = ({ isOpen, property, loading, onClose }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch property analysis when modal opens
  useEffect(() => {
    async function getPropertyAnalysis() {
      if (isOpen && property && !loading) {
        setIsLoading(true);
        setError(null);

        try {
          const analysisData = await fetchPropertyAnalysis(property);
          setSummary(analysisData);
        } catch (err) {
          console.error("Failed to fetch property analysis:", err);
          setError("Unable to generate property analysis. Please try again.");
        } finally {
          setIsLoading(false);
        }
      }
    }

    getPropertyAnalysis();
  }, [isOpen, property, loading]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                AI Property Analysis
              </h2>
              {summary && summary.analysisEngine && (
                <>
                  {summary.analysisEngine === "OpenAI GPT-4" ? (
                    <div className="mt-1 flex items-center">
                      <div className="px-2 py-1 bg-green-100 text-green-600 rounded-full flex items-center font-medium text-xs shadow-sm">
                        <span className="mr-1 relative">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                          </svg>
                          <span
                            className="absolute top-0 left-0 h-full w-full bg-green-400 opacity-75 rounded-full animate-ping"
                            style={{ animationDuration: "3s" }}
                          ></span>
                        </span>
                        Powered by OpenAI GPT-4
                      </div>
                      {summary.note && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({summary.note})
                        </span>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
            {isLoading || loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">
                  Analyzing property with advanced AI...
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  This may take a moment as we evaluate market conditions and
                  investment potential
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <p className="text-red-500">{error}</p>
                <button
                  onClick={() => {
                    setIsLoading(true);
                    setError(null);
                    fetchPropertyAnalysis(property)
                      .then((data) => {
                        setSummary(data);
                        setIsLoading(false);
                      })
                      .catch((err) => {
                        console.error("Retry failed:", err);
                        setError(
                          "Analysis generation failed. Please try again later."
                        );
                        setIsLoading(false);
                      });
                  }}
                  className="mt-4 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Retry
                </button>
              </div>
            ) : summary ? (
              <div className="space-y-6">
                {/* Property info */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    Property Information
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <p>
                        <span className="font-medium">Address:</span>{" "}
                        {property.address}
                      </p>
                      <p>
                        <span className="font-medium">City/State:</span>{" "}
                        {property.city}, {property.state}
                      </p>
                      <p>
                        <span className="font-medium">Upset Value:</span>{" "}
                        {property.upset_value}
                      </p>
                      <p>
                        <span className="font-medium">Zillow Estimate:</span>{" "}
                        {property.marketValueFormatted || "N/A"}
                      </p>
                      <p>
                        <span className="font-medium">Sale Date:</span>{" "}
                        {property.sale_date}
                      </p>
                      <p>
                        <span className="font-medium">Case #:</span>{" "}
                        {property.court_case_no || "N/A"}
                      </p>
                      {property.bedrooms && (
                        <p>
                          <span className="font-medium">Bedrooms:</span>{" "}
                          {property.bedrooms}
                        </p>
                      )}
                      {property.bathrooms && (
                        <p>
                          <span className="font-medium">Bathrooms:</span>{" "}
                          {property.bathrooms}
                        </p>
                      )}
                      {property.livingArea && (
                        <p>
                          <span className="font-medium">Living Area:</span>{" "}
                          {property.livingArea} sq ft
                        </p>
                      )}
                      {property.yearBuilt && (
                        <p>
                          <span className="font-medium">Year Built:</span>{" "}
                          {property.yearBuilt}
                        </p>
                      )}

                      {/* Property Record Link */}
                      {property.property_id && (
                        <p className="col-span-1 md:col-span-2 mt-2">
                          <span className="font-medium">Official Record:</span>{" "}
                          <a
                            href={`https://salesweb.civilview.com/Sales/SaleDetails?PropertyId=${property.property_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View on SalesWeb CivilView
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Overall Analysis */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    Overall Analysis
                  </h3>
                  <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
                    <p className="text-gray-700">{summary.overallAnalysis}</p>
                  </div>
                </div>

                {/* Market Analysis */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    Market Analysis
                  </h3>
                  <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
                    <p className="text-gray-700">{summary.marketAnalysis}</p>
                  </div>
                </div>

                {/* Property Value Analysis - New Section */}
                {summary.propertyValueAnalysis && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                      Property Value Analysis
                    </h3>
                    <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
                      <p className="text-gray-700">
                        {summary.propertyValueAnalysis}
                      </p>
                    </div>
                  </div>
                )}

                {/* Investment Potential */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    Investment Potential
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Profit Potential:</span>
                        <span
                          className={`font-medium ${
                            summary.investmentPotential.profitPotential ===
                            "High"
                              ? "text-green-600"
                              : summary.investmentPotential.profitPotential ===
                                "Medium"
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {summary.investmentPotential.profitPotential}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Risk Level:</span>
                        <span
                          className={`font-medium ${
                            summary.investmentPotential.riskLevel === "Low"
                              ? "text-green-600"
                              : summary.investmentPotential.riskLevel ===
                                "Medium"
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {summary.investmentPotential.riskLevel}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">
                          Neighborhood Trend:
                        </span>
                        <span
                          className={`font-medium ${
                            summary.investmentPotential.neighborhoodTrend ===
                            "Improving"
                              ? "text-green-600"
                              : summary.investmentPotential
                                  .neighborhoodTrend === "Stable"
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {summary.investmentPotential.neighborhoodTrend}
                        </span>
                      </div>
                      {summary.investmentPotential.rentalPotential && (
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-600">
                            Rental Potential:
                          </span>
                          <span className="font-medium text-blue-600">
                            {summary.investmentPotential.rentalPotential}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">
                          Est. Time to Flip:
                        </span>
                        <span className="font-medium">
                          {summary.investmentPotential.estimatedTimeToFlip}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">
                          Est. Repair Costs:
                        </span>
                        <span className="font-medium">
                          {summary.investmentPotential.estimatedRepairCosts}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">
                          Renovation Complexity:
                        </span>
                        <span className="font-medium">
                          {summary.investmentPotential.renovationComplexity}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Time on Market:</span>
                        <span className="font-medium">
                          {summary.investmentPotential.expectedTimeOnMarket}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legal Considerations */}
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">
                    Legal Considerations
                  </h3>
                  <div className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
                    <p className="text-gray-700">
                      {summary.legalConsiderations}
                    </p>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">
                    AI Recommendation
                  </h3>
                  <p className="text-gray-800">{summary.recommendation}</p>
                </div>

                {/* Disclaimer */}
                <div className="text-xs text-gray-500 italic mt-4">
                  <p>
                    Disclaimer: This AI-generated analysis is for informational
                    purposes only and should not be considered financial or
                    legal advice. Always consult with professionals before
                    making investment decisions.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500">
                  Unable to generate property analysis. Please try again.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertySummaryModal;
